import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Globe2,
  Send,
  Loader2,
  Paperclip,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { COMPANY } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TO_PRESTATION = {
  "transport-marchandises": "Transport de marchandises",
  "location-conducteur": "Location avec conducteur",
  demenagement: "Déménagement",
  commissionnaire: "Commissionnaire",
  "transport-vehicules": "Transport de véhicules",
  "transport-exceptionnel": "Autre",
};

const PRESTATIONS = [
  "Transport de marchandises",
  "Location avec conducteur",
  "Déménagement",
  "Commissionnaire",
  "Transport de véhicules",
  "Autre",
];

const VEHICULES = [
  "Je ne sais pas",
  "Fourgon / utilitaire léger",
  "Camion porteur 7,5 T",
  "Camion porteur 12 T",
  "Camion porteur 19 T",
  "Semi-remorque 40 T",
  "Porte-voitures / plateau",
];

const initialDevis = {
  profil: "",
  prestation: "",
  societe: "",
  nom: "",
  email: "",
  telephone: "",
  adresseEnlevement: "",
  adresseLivraison: "",
  dateEnlevement: "",
  natureMarchandise: "",
  poids: "",
  volume: "",
  vehicule: "Je ne sais pas",
  optDemenagement: false,
  optHayon: false,
  optUrgence: false,
  optAutre: false,
  infos: "",
  rgpd: false,
  prospection: false,
  website: "", // honeypot
};

const Contact = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState("devis");

  const [devis, setDevis] = useState(initialDevis);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [contact, setContact] = useState({ nom: "", email: "", sujet: "", message: "", rgpd: false, website: "" });
  const [contactErrors, setContactErrors] = useState({});
  const [contactSending, setContactSending] = useState(false);
  const [contactError, setContactError] = useState("");

  // Pré-remplissage depuis le calculateur (state) ou une carte service (?service=)
  useEffect(() => {
    const prefill = location.state?.prefill;
    const params = new URLSearchParams(location.search);
    const serviceId = params.get("service");

    setDevis((d) => {
      const next = { ...d };
      if (serviceId && SERVICE_TO_PRESTATION[serviceId]) {
        next.prestation = SERVICE_TO_PRESTATION[serviceId];
      }
      if (prefill) {
        if (prefill.prestation) next.prestation = prefill.prestation;
        if (prefill.adresseEnlevement) next.adresseEnlevement = prefill.adresseEnlevement;
        if (prefill.adresseLivraison) next.adresseLivraison = prefill.adresseLivraison;
        if (prefill.poids) next.poids = prefill.poids;
        if (prefill.vehicule) {
          const found = VEHICULES.find((v) => prefill.vehicule.startsWith(v.slice(0, 12)));
          if (found) next.vehicule = found;
        }
        if (prefill.options) {
          next.optDemenagement = !!prefill.options.demenagement;
          next.optHayon = !!prefill.options.hayon;
          next.optUrgence = !!prefill.options.urgence;
        }
        const extras = [];
        if (prefill.distanceKm) extras.push(`Distance estimée : ${prefill.distanceKm} km`);
        if (prefill.estimation) extras.push(`Estimation calculateur : ${prefill.estimation}`);
        if (extras.length) next.infos = extras.join("\n");
      }
      return next;
    });
    if (prefill || serviceId) setTab("devis");
  }, [location.state, location.search]);

  const set = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setDevis((d) => ({ ...d, [key]: val }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []);
    const valid = list
      .filter((f) => /\.(jpe?g|png|pdf)$/i.test(f.name) && f.size <= 5 * 1024 * 1024)
      .slice(0, 5);
    setFiles(valid);
  };

  const validateDevis = () => {
    const err = {};
    if (!devis.profil) err.profil = "Champ obligatoire";
    if (!devis.prestation) err.prestation = "Champ obligatoire";
    if (!devis.nom.trim()) err.nom = "Champ obligatoire";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(devis.email)) err.email = "Email invalide";
    if (!devis.telephone.trim()) err.telephone = "Champ obligatoire";
    if (!devis.adresseEnlevement.trim()) err.adresseEnlevement = "Champ obligatoire";
    if (!devis.adresseLivraison.trim()) err.adresseLivraison = "Champ obligatoire";
    if (!devis.natureMarchandise.trim()) err.natureMarchandise = "Champ obligatoire";
    if (!devis.poids || Number(devis.poids) < 1) err.poids = "Poids obligatoire (kg)";
    if (!devis.rgpd) err.rgpd = "Consentement obligatoire";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submitDevis = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validateDevis()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(devis));
      fd.append("honeypot", devis.website);
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/devis`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Échec de l'envoi");
      navigate("/merci", { state: { type: "devis" } });
    } catch {
      setSubmitError(
        `L'envoi a échoué. Réessayez ou appelez-nous au ${COMPANY.phone}.`
      );
    } finally {
      setSending(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setContactError("");
    const err = {};
    if (!contact.nom.trim()) err.nom = "Champ obligatoire";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email)) err.email = "Email invalide";
    if (!contact.sujet.trim()) err.sujet = "Champ obligatoire";
    if (!contact.message.trim()) err.message = "Champ obligatoire";
    if (!contact.rgpd) err.rgpd = "Consentement obligatoire";
    setContactErrors(err);
    if (Object.keys(err).length) return;
    setContactSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nom: contact.nom,
          email: contact.email,
          sujet: contact.sujet,
          message: contact.message,
          rgpd: contact.rgpd,
        }),
      });
      if (!res.ok) throw new Error("Échec de l'envoi");
      navigate("/merci", { state: { type: "contact" } });
    } catch {
      setContactError(`L'envoi a échoué. Réessayez ou écrivez à ${COMPANY.email}.`);
    } finally {
      setContactSending(false);
    }
  };

  const inputCls = (err) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-base text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
      err
        ? "border-red-400 focus:border-red-500 focus:ring-red-200"
        : "border-slate-300 focus:border-primary focus:ring-primary/30"
    }`;
  const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";
  const errCls = "mt-1 text-xs font-medium text-red-600";

  const mapSrc = useMemo(
    () =>
      "https://www.openstreetmap.org/export/embed.html?bbox=4.0089%2C48.3036%2C4.0689%2C48.3336&layer=mapnik&marker=48.3186%2C4.0389",
    []
  );

  return (
    <>
      <Seo
        title="Contact et demande de devis | ASTROLOG"
        description="Contactez ASTROLOG pour votre transport, location avec conducteur ou déménagement. Réponse sous 24 h ouvrées. Tél. 07 68 34 37 33 — La Chapelle-Saint-Luc (Aube)."
        path="/contact"
      />

      <section className="brand-dark noise relative py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h1 className="font-heading text-4xl font-bold tracking-tighter text-[#FAFAF7] sm:text-5xl">
              Contact &amp; demande de devis
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              Décrivez votre besoin : nous vous répondons sous 24 heures ouvrées
              avec un devis gratuit et sans engagement.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-5">
          {/* Coordonnées */}
          <Reveal className="lg:col-span-2">
            <div className="space-y-6">
              <div className="rounded-3xl border border-slate-200 bg-white p-7">
                <h2 className="font-heading text-xl font-semibold">Nos coordonnées</h2>
                <ul className="mt-5 space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <span>{COMPANY.address}, {COMPANY.postalCode} {COMPANY.city}</span>
                  </li>
                  <li>
                    <a href={COMPANY.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-3 font-semibold text-foreground transition-colors hover:text-primary">
                      <Phone className="h-5 w-5 shrink-0 text-primary" />
                      {COMPANY.phone}
                    </a>
                  </li>
                  <li>
                    <a href={COMPANY.emailHref} data-testid="contact-email-link" className="flex items-center gap-3 transition-colors hover:text-primary">
                      <Mail className="h-5 w-5 shrink-0 text-primary" />
                      {COMPANY.email}
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <span>{COMPANY.hours}.<br />Fermé dimanches et jours fériés.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Globe2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <span>{COMPANY.zone}</span>
                  </li>
                </ul>
              </div>
              <div className="overflow-hidden rounded-3xl border border-slate-200">
                <iframe
                  title="Carte — ASTROLOG, La Chapelle-Saint-Luc"
                  src={mapSrc}
                  className="h-72 w-full border-0"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </Reveal>

          {/* Formulaires */}
          <Reveal delay={120} className="lg:col-span-3">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="grid w-full grid-cols-2" data-testid="contact-tabs">
                  <TabsTrigger value="devis" data-testid="tab-devis">Demande de devis</TabsTrigger>
                  <TabsTrigger value="contact" data-testid="tab-contact">Message simple</TabsTrigger>
                </TabsList>

                {/* ===== DEVIS ===== */}
                <TabsContent value="devis">
                  <form onSubmit={submitDevis} noValidate className="mt-6 space-y-5" data-testid="devis-form">
                    {/* Honeypot anti-spam (invisible) */}
                    <div className="hp-field" aria-hidden="true">
                      <label htmlFor="website">Ne pas remplir</label>
                      <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={devis.website} onChange={set("website")} />
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="profil" className={labelCls}>Vous êtes *</label>
                        <select id="profil" data-testid="devis-profil" value={devis.profil} onChange={set("profil")} className={inputCls(errors.profil)}>
                          <option value="">Sélectionnez…</option>
                          <option>Particulier</option>
                          <option>Professionnel</option>
                          <option>Autre</option>
                        </select>
                        {errors.profil && <p className={errCls} role="alert">{errors.profil}</p>}
                      </div>
                      <div>
                        <label htmlFor="prestation" className={labelCls}>Type de prestation *</label>
                        <select id="prestation" data-testid="devis-prestation" value={devis.prestation} onChange={set("prestation")} className={inputCls(errors.prestation)}>
                          <option value="">Sélectionnez…</option>
                          {PRESTATIONS.map((p) => <option key={p}>{p}</option>)}
                        </select>
                        {errors.prestation && <p className={errCls} role="alert">{errors.prestation}</p>}
                      </div>
                      <div>
                        <label htmlFor="societe" className={labelCls}>Société (si professionnel)</label>
                        <input id="societe" data-testid="devis-societe" type="text" value={devis.societe} onChange={set("societe")} className={inputCls()} placeholder="Nom de votre société" />
                      </div>
                      <div>
                        <label htmlFor="nom" className={labelCls}>Nom et prénom *</label>
                        <input id="nom" data-testid="devis-nom" type="text" value={devis.nom} onChange={set("nom")} className={inputCls(errors.nom)} placeholder="Jean Dupont" />
                        {errors.nom && <p className={errCls} role="alert">{errors.nom}</p>}
                      </div>
                      <div>
                        <label htmlFor="email" className={labelCls}>Email *</label>
                        <input id="email" data-testid="devis-email" type="email" value={devis.email} onChange={set("email")} className={inputCls(errors.email)} placeholder="jean@exemple.fr" />
                        {errors.email && <p className={errCls} role="alert">{errors.email}</p>}
                      </div>
                      <div>
                        <label htmlFor="telephone" className={labelCls}>Téléphone *</label>
                        <input id="telephone" data-testid="devis-telephone" type="tel" value={devis.telephone} onChange={set("telephone")} className={inputCls(errors.telephone)} placeholder="06 12 34 56 78" />
                        {errors.telephone && <p className={errCls} role="alert">{errors.telephone}</p>}
                      </div>
                      <div>
                        <label htmlFor="adresseEnlevement" className={labelCls}>Adresse d'enlèvement *</label>
                        <input id="adresseEnlevement" data-testid="devis-adresse-enlevement" type="text" value={devis.adresseEnlevement} onChange={set("adresseEnlevement")} className={inputCls(errors.adresseEnlevement)} placeholder="Ville ou adresse complète" />
                        {errors.adresseEnlevement && <p className={errCls} role="alert">{errors.adresseEnlevement}</p>}
                      </div>
                      <div>
                        <label htmlFor="adresseLivraison" className={labelCls}>Adresse de livraison *</label>
                        <input id="adresseLivraison" data-testid="devis-adresse-livraison" type="text" value={devis.adresseLivraison} onChange={set("adresseLivraison")} className={inputCls(errors.adresseLivraison)} placeholder="Ville ou adresse complète" />
                        {errors.adresseLivraison && <p className={errCls} role="alert">{errors.adresseLivraison}</p>}
                      </div>
                      <div>
                        <label htmlFor="dateEnlevement" className={labelCls}>Date souhaitée d'enlèvement</label>
                        <input id="dateEnlevement" data-testid="devis-date" type="date" value={devis.dateEnlevement} onChange={set("dateEnlevement")} className={inputCls()} />
                      </div>
                      <div>
                        <label htmlFor="natureMarchandise" className={labelCls}>Nature de la marchandise *</label>
                        <input id="natureMarchandise" data-testid="devis-nature" type="text" value={devis.natureMarchandise} onChange={set("natureMarchandise")} className={inputCls(errors.natureMarchandise)} placeholder="Ex. : 4 palettes de carrelage" />
                        {errors.natureMarchandise && <p className={errCls} role="alert">{errors.natureMarchandise}</p>}
                      </div>
                      <div>
                        <label htmlFor="poids" className={labelCls}>Poids total (kg) *</label>
                        <input id="poids" data-testid="devis-poids" type="number" min="1" value={devis.poids} onChange={set("poids")} className={inputCls(errors.poids)} placeholder="Ex. : 800" />
                        {errors.poids && <p className={errCls} role="alert">{errors.poids}</p>}
                      </div>
                      <div>
                        <label htmlFor="volume" className={labelCls}>Volume ou dimensions</label>
                        <input id="volume" data-testid="devis-volume" type="text" value={devis.volume} onChange={set("volume")} className={inputCls()} placeholder="Ex. : 12 m³ ou 120×80×150 cm" />
                      </div>
                      <div>
                        <label htmlFor="vehicule" className={labelCls}>Véhicule souhaité</label>
                        <select id="vehicule" data-testid="devis-vehicule" value={devis.vehicule} onChange={set("vehicule")} className={inputCls()}>
                          {VEHICULES.map((v) => <option key={v}>{v}</option>)}
                        </select>
                      </div>
                    </div>

                    <fieldset>
                      <legend className={labelCls}>Options</legend>
                      <div className="flex flex-wrap gap-x-6 gap-y-3">
                        {[
                          ["optDemenagement", "Déménagement complet", "devis-opt-demenagement"],
                          ["optHayon", "Hayon élévateur", "devis-opt-hayon"],
                          ["optUrgence", "Urgence (< 24 h)", "devis-opt-urgence"],
                          ["optAutre", "Autre besoin", "devis-opt-autre"],
                        ].map(([key, label, tid]) => (
                          <label key={key} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" data-testid={tid} checked={devis[key]} onChange={set(key)} className="h-4 w-4 rounded border-slate-300 accent-[#2563EB]" />
                            {label}
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <div>
                      <label htmlFor="infos" className={labelCls}>Informations complémentaires</label>
                      <textarea id="infos" data-testid="devis-infos" rows={4} value={devis.infos} onChange={set("infos")} className={inputCls()} placeholder="Accès, étage, contraintes horaires…" />
                    </div>

                    <div>
                      <label htmlFor="fichiers" className={labelCls}>Photos ou documents (facultatif)</label>
                      <label
                        htmlFor="fichiers"
                        data-testid="devis-files-label"
                        className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500 transition-colors hover:border-primary hover:text-primary"
                      >
                        <Paperclip className="h-5 w-5" />
                        {files.length
                          ? `${files.length} fichier(s) : ${files.map((f) => f.name).join(", ")}`
                          : "JPG, PNG ou PDF — 5 Mo max, 5 fichiers max"}
                      </label>
                      <input id="fichiers" data-testid="devis-files" type="file" multiple accept=".jpg,.jpeg,.png,.pdf" onChange={handleFiles} className="sr-only" />
                    </div>

                    <div className="space-y-3">
                      <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700">
                        <input type="checkbox" data-testid="devis-rgpd" checked={devis.rgpd} onChange={set("rgpd")} className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#2563EB]" />
                        <span>
                          J'accepte que mes données soient traitées pour l'établissement de mon devis, conformément à la{" "}
                          <a href="/politique-de-confidentialite" className="font-medium text-primary underline">politique de confidentialité</a>. *
                        </span>
                      </label>
                      {errors.rgpd && <p className={errCls} role="alert">{errors.rgpd}</p>}
                      <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700">
                        <input type="checkbox" data-testid="devis-prospection" checked={devis.prospection} onChange={set("prospection")} className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#2563EB]" />
                        <span>J'accepte de recevoir des informations commerciales d'ASTROLOG (facultatif).</span>
                      </label>
                    </div>

                    {submitError && (
                      <p role="alert" data-testid="devis-submit-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
                        {submitError}
                      </p>
                    )}

                    <button
                      type="submit"
                      data-testid="devis-submit-button"
                      disabled={sending}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-8 py-4 font-heading text-base font-semibold text-white shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#EA580C] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                      {sending ? "Envoi en cours…" : "Envoyer ma demande de devis"}
                    </button>
                  </form>
                </TabsContent>

                {/* ===== CONTACT SIMPLE ===== */}
                <TabsContent value="contact">
                  <form onSubmit={submitContact} noValidate className="mt-6 space-y-5" data-testid="contact-form">
                    <div className="hp-field" aria-hidden="true">
                      <label htmlFor="website2">Ne pas remplir</label>
                      <input id="website2" type="text" tabIndex={-1} autoComplete="off" value={contact.website} onChange={(e) => setContact((c) => ({ ...c, website: e.target.value }))} />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="c-nom" className={labelCls}>Nom *</label>
                        <input id="c-nom" data-testid="contact-nom" type="text" value={contact.nom} onChange={(e) => setContact((c) => ({ ...c, nom: e.target.value }))} className={inputCls(contactErrors.nom)} />
                        {contactErrors.nom && <p className={errCls} role="alert">{contactErrors.nom}</p>}
                      </div>
                      <div>
                        <label htmlFor="c-email" className={labelCls}>Email *</label>
                        <input id="c-email" data-testid="contact-email" type="email" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} className={inputCls(contactErrors.email)} />
                        {contactErrors.email && <p className={errCls} role="alert">{contactErrors.email}</p>}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="c-sujet" className={labelCls}>Sujet *</label>
                      <input id="c-sujet" data-testid="contact-sujet" type="text" value={contact.sujet} onChange={(e) => setContact((c) => ({ ...c, sujet: e.target.value }))} className={inputCls(contactErrors.sujet)} />
                      {contactErrors.sujet && <p className={errCls} role="alert">{contactErrors.sujet}</p>}
                    </div>
                    <div>
                      <label htmlFor="c-message" className={labelCls}>Message *</label>
                      <textarea id="c-message" data-testid="contact-message" rows={6} value={contact.message} onChange={(e) => setContact((c) => ({ ...c, message: e.target.value }))} className={inputCls(contactErrors.message)} />
                      {contactErrors.message && <p className={errCls} role="alert">{contactErrors.message}</p>}
                    </div>
                    <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700">
                      <input type="checkbox" data-testid="contact-rgpd" checked={contact.rgpd} onChange={(e) => setContact((c) => ({ ...c, rgpd: e.target.checked }))} className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#2563EB]" />
                      <span>
                        J'accepte le traitement de mes données conformément à la{" "}
                        <a href="/politique-de-confidentialite" className="font-medium text-primary underline">politique de confidentialité</a>. *
                      </span>
                    </label>
                    {contactErrors.rgpd && <p className={errCls} role="alert">{contactErrors.rgpd}</p>}
                    {contactError && (
                      <p role="alert" data-testid="contact-submit-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
                        {contactError}
                      </p>
                    )}
                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={contactSending}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-heading text-base font-semibold text-white shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#1D4ED8] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {contactSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                      {contactSending ? "Envoi en cours…" : "Envoyer le message"}
                    </button>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
